import { PlanogramObjectAttributes } from "./Resources";
import { PlanogramObject } from "./PlanogramObject.js";
// import { PlanogramSubcomponent  } from "./PlanogramSubcomponent.js";
import { PlanogramSubcomponent } from "./PlanogramSubcomponent.js";
import { PlanogramRenderer } from "./PlanogramRenderer.js";

import * as THREE from 'three';


export class PlanogramComponent extends PlanogramObject {

    //#region Attributes 
    static Attributes = {
        "Type": "type",
        "EditCustom": "edit-custom"
    };
    
    //#endregion

    static get observedAttributes(){
        return [
            ...Object.values(PlanogramComponent.Attributes),
            ...Object.values(PlanogramObjectAttributes.Attributes),
        ];
    }

    get groupBoundingBox() {
        return this.#subComponents.reduce( (a,c) => a.union(c.groupBoundingBox), new THREE.Box3());
    }

    get type() { 
        if(!this.hasAttribute(PlanogramComponent.Attributes.Type)) return PlanogramComponent.Types.Shelf;
        var attributeval = this.getAttribute(PlanogramComponent.Attributes.Type);
        for(var [key, value] of Object.entries(PlanogramComponent.Types)){
            if(key.toLocaleLowerCase() === attributeval.toLocaleLowerCase())
                return value;
        }
        return PlanogramComponent.Types.Shelf; 
    }

    get editCustom() { return this.hasAttribute(PlanogramComponent.Attributes.EditCustom); }

    get hideChestwalls(){
        
    }

    /** @returns {PlanogramSubcomponent[]}  */
    get #subComponents(){
        return [...this.querySelectorAll("planogram-subcomponent")].filter(x=>Reflect.has(x, "firstRender") && !x.firstRender && x.isConnected);
    }

    get totalMeshCount(){
        return this.#subComponents.reduce( (a,c) => a + c.totalMeshCount, 0);
    } 

    /** @type {THREE.BufferGeometry} */
    #geometry = null;

    /** @type {THREE.InstancedMesh} */      
    #mesh = null;

    constructor(){
        super();
    }


    #generateGeometry() {
        this.#geometry?.dispose();
        this.#geometry = new THREE.BoxGeometry(1, 1, 1);
        this.#geometry.computeBoundingBox();
        this.#geometry.center();
    }

    #generateMeshes(){
        
        if(!this.#mesh || this.#mesh.count !== this.totalMeshCount ){
            if (this.#mesh) this.#mesh.removeFromParent();
            const shader = PlanogramRenderer.get(this).resources.SubcomponentShader;
            this.#mesh = new THREE.InstancedMesh( this.#geometry, shader, this.totalMeshCount )
            this.addMesh(this.#mesh);
        }

        const subComponents = this.#subComponents;
        if(!subComponents.length) return;

        subComponents.reduce((a,c)=>{
            c.generateSubcomponent(this.#mesh, a );
            return a + c.totalMeshCount;
        }, 0);

        this.setDecoratorGeometrySize( true );

        this.#setAttributes();
        
    }

    #setAttributes(){

        const subComponents = this.#subComponents;
        /** @type {THREE.Vector4[]} */
        const sizes = subComponents.reduce((a,c)=>[ ...a, ...c.sizes ], []);
        /** @type {THREE.Vector4[][]} */
        const [ frontBackFill, topBottomFill, leftRightFill ] = subComponents.reduce((a,c)=>{
            const [ frontBackFill, topBottomFill, leftRightFill ] = c.colours;
            a[0].push(...frontBackFill);
            a[1].push(...topBottomFill);
            a[2].push(...leftRightFill);
            return a;
        }, [[],[],[]]);
        /** @type {THREE.Vector4[]} */
        const options = subComponents.reduce((a,c)=>[ ...a, ...c.options ], []);
        /** @type {THREE.Vector4[][]} */
        const [ drawArgs1, drawArgs2, drawArgs3 ] = subComponents.reduce((a,c)=>{
            const [ drawArgs1, drawArgs2, drawArgs3 ] = c.drawArgs;
            a[0].push(...drawArgs1);
            a[1].push(...drawArgs2);
            a[2].push(...drawArgs3);
            return a;
        }, [[],[],[]]);

        const fromVector4Array = (arr) => arr.reduce( (a,c) => [...a, c.x, c.y, c.z, c.w], []);
        
        this.#mesh.geometry.setAttribute("size", new THREE.InstancedBufferAttribute(new Float32Array(fromVector4Array(sizes)), 4));
        this.#mesh.geometry.setAttribute("options", new THREE.InstancedBufferAttribute(new Float32Array(fromVector4Array(options)), 4));
        this.#mesh.geometry.setAttribute("frontBackFill", new THREE.InstancedBufferAttribute(new Float32Array(fromVector4Array(frontBackFill)), 4));
        this.#mesh.geometry.setAttribute("topBottomFill", new THREE.InstancedBufferAttribute(new Float32Array(fromVector4Array(topBottomFill)), 4));
        this.#mesh.geometry.setAttribute("leftRightFill", new THREE.InstancedBufferAttribute(new Float32Array(fromVector4Array(leftRightFill)), 4));
        this.#mesh.geometry.setAttribute("drawingArgs1", new THREE.InstancedBufferAttribute(new Float32Array(fromVector4Array(drawArgs1)), 4));
        this.#mesh.geometry.setAttribute("drawingArgs2", new THREE.InstancedBufferAttribute(new Float32Array(fromVector4Array(drawArgs2)), 4));
        this.#mesh.geometry.setAttribute("drawingArgs3", new THREE.InstancedBufferAttribute(new Float32Array(fromVector4Array(drawArgs3)), 4));
        
        this.#mesh.instanceMatrix.needsUpdate = true;
    }

    subComponentPlug( reMesh = false ){
        if(this.isConnected){
            if(reMesh) this.#generateMeshes();
            else this.#setAttributes();
        }
    }

    /**
     * 
     * @param {THREE.Intersection} intersection 
     */
    getTargetedSubComponent( intersection )
    {
        if(!intersection || !intersection.object || intersection.object !== this.#mesh) return null;
        const instanceId = intersection.instanceId;
        if(instanceId === undefined || instanceId < 0) return null;
        let count = 0;
        for(const subComponent of this.#subComponents){
            if(instanceId < count + subComponent.totalMeshCount) return subComponent;
            count += subComponent.totalMeshCount;
        }
    }

    connectedCallback() {
        super.connectedCallback();
        this.firstRender = false;
        this.#generateGeometry();
        this.#generateMeshes();

    }


    attributeChangedCallback(name, oldValue, newValue) {
        if (this.firstRender || oldValue === newValue ) return;
        super.attributeChangedCallback(name, oldValue, newValue);
        if(name === PlanogramComponent.Attributes.Type){
            this.#generateMeshes();
        }
    }

    disconnectedCallback() {
        super.disconnectedCallback();
    }

    dispose(){
        super.dispose();
        this.#mesh.removeFromParent();
        this.#mesh = null;
        this.#geometry.dispose();
        this.#geometry = null;
    }

    //#region Static Properties
    static Types = {
        Custom : 0,
        Shelf : 1,
        Bar : 2,
        Peg : 3,
        Chest : 4,
        Backboard : 5,
        Panel : 6,
        Base : 7,
        Rod : 8,
        ClipStrip : 9,
        Pallet : 10,
        SlotWall : 11,
        
        Shelf_Riser : 12,
        Divider: 13,
        PegProng: 14,
        PegTag: 15,
        Chest_Wall: 16,
    }
    //#endregion

}